import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { get, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PayrollConfig() {
  const navigate = useNavigate();
  const { me } = useApp();
  const { data: config, isLoading } = useQuery({ queryKey: ["payroll-config"], queryFn: () => get("/hrms/payroll/config") });
  const [form, setForm] = useState(null);
  useEffect(() => { if (config) setForm(config); }, [config]);

  const save = useMutation({
    mutationFn: (body) => patch("/hrms/payroll/config", { ...body, _actor: me?.id }),
    onSuccess: () => toast.success("Payroll configuration saved"),
  });

  if (isLoading || !form) return <Skeleton className="h-64" />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: Number(v) }));

  return (
    <div className="max-w-xl">
      <PageHeader title="Payroll Configuration" subtitle="These are admin-configurable defaults, not statutory-rule-engine calculations — confirm real PF/ESI/tax rules for your region before relying on this for actual payroll." />
      <Section>
        <div className="grid grid-cols-2 gap-4 p-4">
          <div><Label>Standard Days per Month</Label><Input data-testid="cfg-std-days" type="number" className="mt-1" value={form.standard_days_per_month} onChange={(e) => set("standard_days_per_month", e.target.value)} /></div>
          <div><Label>Paid Leave Allowance (days)</Label><Input data-testid="cfg-allowance" type="number" step="0.5" className="mt-1" value={form.paid_leave_allowance_days} onChange={(e) => set("paid_leave_allowance_days", e.target.value)} /></div>
          <div><Label>Provident Fund %</Label><Input data-testid="cfg-pf" type="number" step="0.1" className="mt-1" value={form.pf_pct} onChange={(e) => set("pf_pct", e.target.value)} /></div>
          <div><Label>ESI %</Label><Input data-testid="cfg-esi" type="number" step="0.1" className="mt-1" value={form.esi_pct} onChange={(e) => set("esi_pct", e.target.value)} /></div>
          <div><Label>Income Tax %</Label><Input data-testid="cfg-tax" type="number" step="0.1" className="mt-1" value={form.tax_pct} onChange={(e) => set("tax_pct", e.target.value)} /></div>
        </div>
      </Section>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate("/hrms/payroll")}>Back</Button>
        <Button data-testid="save-payroll-config" onClick={() => save.mutate(form)} disabled={save.isPending}>Save</Button>
      </div>
    </div>
  );
}
